/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect } from "react";
import { PageShell } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Envelope, PaperPlaneTilt, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ContactUsPage() {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (user) {
      setName((prev) => prev || user.name || "");
      setEmail((prev) => prev || user.email || "");
    }
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post("/contact", {
        name: name.trim(),
        email: email.trim(),
        subject: subject.trim() || null,
        message: message.trim(),
      });
      setSent(true);
      toast.success("Thank you — your message has reached us. We'll reply within 24 hours.");
      setSubject("");
      setMessage("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell>
      <main className="mx-auto max-w-3xl px-4 sm:px-6 py-12 sm:py-20">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full label-eyebrow text-muted bg-amber-50 border border-amber-200">
            <Envelope size={11} weight="bold" /> Contact us
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-5 leading-tight">
            Have a <span className="italic text-gold">query?</span>
          </h1>
          <p className="text-muted mt-4 max-w-xl mx-auto text-base leading-relaxed">
            Whether it's a refund, an astrologer match, a technical hiccup, or just curious
            about a tool — drop us a note. We read every message and reply within 24 hours,
            usually faster.
          </p>
        </div>

        {sent ? (
          <div className="mt-10 rounded-2xl p-8 sm:p-10 bg-emerald-50 border border-emerald-200 text-center"
            data-testid="contact-sent-card">
            <Sparkle size={36} weight="fill" className="mx-auto text-emerald-600 mb-3" />
            <div className="font-serif-display text-2xl text-[#1F3F2D]">Message received 🙏</div>
            <p className="text-emerald-800 text-sm mt-2 max-w-md mx-auto">
              We've notified the team and a copy has been forwarded to our inbox.
              Expect a reply at <b>{email}</b> within 24 hours.
            </p>
            <button onClick={() => setSent(false)}
              className="mt-6 btn-ghost rounded-full px-5 py-2 text-sm font-medium"
              data-testid="contact-send-another">
              Send another message
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 rounded-2xl p-6 sm:p-8 bg-white border border-soft shadow-sm space-y-5"
            data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="label-eyebrow text-muted block mb-1.5">Your name</label>
                <input type="text" required value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Asha Kumari"
                  className="w-full px-4 py-2.5 rounded-lg border border-soft focus:border-amber-400 focus:outline-none text-sm"
                  data-testid="contact-input-name" />
              </div>
              <div>
                <label className="label-eyebrow text-muted block mb-1.5">Email</label>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="w-full px-4 py-2.5 rounded-lg border border-soft focus:border-amber-400 focus:outline-none text-sm"
                  data-testid="contact-input-email" />
              </div>
            </div>
            <div>
              <label className="label-eyebrow text-muted block mb-1.5">Subject (optional)</label>
              <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                placeholder="Question about my chat with Surekha Joshi"
                className="w-full px-4 py-2.5 rounded-lg border border-soft focus:border-amber-400 focus:outline-none text-sm"
                data-testid="contact-input-subject" />
            </div>
            <div>
              <label className="label-eyebrow text-muted block mb-1.5">
                Your message <span className="text-muted">· {message.length}/4000</span>
              </label>
              <textarea required rows={6} maxLength={4000} value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us what's on your mind…"
                className="w-full px-4 py-3 rounded-lg border border-soft focus:border-amber-400 focus:outline-none text-sm leading-relaxed"
                data-testid="contact-input-message" />
            </div>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-muted">
                We never share your details. Replies land in <b>{email || "your inbox"}</b>.
              </p>
              <button type="submit" disabled={submitting || message.trim().length < 8}
                className="btn-gold inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-medium disabled:opacity-50"
                data-testid="contact-submit">
                {submitting ? "Sending…" : (<><PaperPlaneTilt size={14} weight="bold" /> Send message</>)}
              </button>
            </div>
          </form>
        )}
      </main>
    </PageShell>
  );
}
